import { QueryClient } from '@tanstack/react-query';
import { APIError } from '@/lib/api-client';

/**
 * Global error handler for React Query
 */
function onQueryError(error: Error) {
  // Solo logear errores que no sean cancellations
  if (error.message && error.message.includes('CancelledError')) {
    return;
  }

  // Handle 401 Unauthorized - redirect to login
  // Don't clear tokens or redirect if running inside an iframe (e.g., heatmap preview)
  if (error instanceof APIError && error.status === 401 && window.self === window.top) {
    // Clear tokens
    localStorage.removeItem('firebase_token');
    localStorage.removeItem('owly_access_token');
    localStorage.removeItem('owly_refresh_token');
    localStorage.removeItem('owly_user');

    // Redirect to login
    if (!window.location.pathname.includes('/login')) {
      window.location.href = '/login';
    }
    return;
  }

  // Handle 403 with token error (Firebase verification failed and retry in api-client also failed)
  // This is a safety net - api-client should have already handled this and redirected,
  // but if somehow the error reaches here, ensure we redirect to login
  if (error instanceof APIError && error.status === 403 && window.self === window.top) {
    const errorStr = JSON.stringify(error.data || {});
    const isTokenError = errorStr.includes('token_not_valid') ||
      errorStr.includes('Firebase token invalid') ||
      errorStr.includes('Unable to verify Firebase token');

    if (isTokenError) {
      localStorage.removeItem('firebase_token');
      localStorage.removeItem('owly_access_token');
      localStorage.removeItem('owly_refresh_token');
      localStorage.removeItem('owly_user');

      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
      return;
    }
  }

  // Log error

  // TODO: Send to error tracking service (Sentry, LogRocket, etc.)
  // Example:
  // if (import.meta.env.PROD) {
  //   Sentry.captureException(error, {
  //     tags: { type: 'react-query-error' },
  //   });
  // }
}

/**
 * Determinar si un error de QUERY debe ser retry-able
 * Solo para operaciones de lectura (GET)
 */
function shouldRetryQuery(failureCount: number, error: Error): boolean {
  // No reintentar si es CancelledError
  if (error.message && error.message.includes('CancelledError')) {
    return false;
  }

  // Límite de intentos
  if (failureCount > 3) {
    return false;
  }

  // No reintentar errores 404, 401, 403, 400 (no van a cambiar)
  if (error instanceof APIError) {
    if ([404, 401, 403, 400].includes(error.status)) {
      return false;
    }
  }

  // Errores de red y 5xx se pueden reintentar
  return true;
}

/**
 * Determinar si un error de MUTATION debe ser retry-able
 *
 * IMPORTANTE: Las mutations (POST, PUT, DELETE) NO deben reintentarse
 * automáticamente porque pueden causar duplicados o efectos secundarios.
 *
 * Excepciones: Solo reintentar en errores de red (502, 503, 504) donde
 * sabemos que el servidor no procesó la petición.
 */
function shouldRetryMutation(failureCount: number, error: Error): boolean {
  // NUNCA reintentar más de 1 vez
  if (failureCount > 0) {
    return false;
  }

  // SOLO reintentar errores de gateway/servidor que indican que
  // la petición probablemente no llegó al servidor
  if (error instanceof APIError) {
    // 502 Bad Gateway, 503 Service Unavailable, 504 Gateway Timeout
    // Estos errores generalmente significan que el servidor no procesó la petición
    if ([502, 503, 504].includes(error.status)) {
      return true;
    }
    // Cualquier otro error HTTP: NO reintentar
    return false;
  }

  // Errores de red (fetch failed, timeout): NO reintentar
  // porque no sabemos si el servidor recibió la petición
  return false;
}

/**
 * Configuración global de React Query
 *
 * Features:
 * - Cache por 5 minutos
 * - Refetch automático al volver a la ventana
 * - Retry inteligente con backoff exponencial
 * - Stale time de 1 minuto para reducir requests innecesarios
 * - Error handling global con logging
 * - Persistencia en localStorage (opcional)
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache data por 5 minutos
      gcTime: 1000 * 60 * 5,

      // Considerar data stale después de 1 minuto
      staleTime: 1000 * 60,

      // Refetch al volver a la ventana
      refetchOnWindowFocus: true,

      // Refetch al reconectar
      refetchOnReconnect: true,

      // No refetch al montar si data está fresca
      refetchOnMount: false,

      // Retry con backoff exponencial (solo para queries/GET)
      retry: shouldRetryQuery,

      // Delay exponencial entre retries (1s, 2s, 4s, max 30s)
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

      // No lanzar errores automáticamente (manejamos con isError)
      throwOnError: false,

      // Error handler global
      onError: onQueryError,
    },
    mutations: {
      // Retry mutations solo en errores de gateway (502, 503, 504)
      // NUNCA reintentar operaciones de creación/modificación por defecto
      // para evitar duplicados
      retry: shouldRetryMutation,

      // Error handler global para mutations
      onError: onQueryError,
    },
  },
});

// ⚠️ PERSISTENCIA DESHABILITADA TEMPORALMENTE
// Causaba errores de CancelledError con cache corrupto

// Remove only the cache key, NOT localStorage.clear() which would wipe auth tokens
try {
  localStorage.removeItem('OWLY_CRM_CACHE');
} catch (e) {
}
